import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef, useState } from 'react';

const THREAD_KEY = 'ahafood.chat.thread';
const THIRTY_MINUTES = 30 * 60 * 1000;

interface ThreadInfo {
  threadId: string;
  userId: string;
  lastMessageTime: number;
}

export function useChatThread(
  userId: string | null,
  lastMessageTime: number | null
) {
  const [threadId, setThreadId] = useState<string | null>(null);
  const lastUserId = useRef<string | null>(null);

  // Load thread info from localStorage
  useEffect(() => {
    if (!userId) {
      setThreadId(null);
      return;
    }
    const raw = localStorage.getItem(THREAD_KEY);
    let info: ThreadInfo | null = null;
    if (raw) {
      try {
        info = JSON.parse(raw);
      } catch {
        /* empty */
      }
    }
    const now = Date.now();
    const shouldCreateNewThread =
      (!info ||
        info.userId !== userId ||
        (lastMessageTime && now - lastMessageTime > THIRTY_MINUTES)) ??
      (!lastMessageTime &&
        info?.lastMessageTime &&
        now - info.lastMessageTime > THIRTY_MINUTES);

    if (shouldCreateNewThread) {
      // Xoá messages của thread cũ nếu có
      if (info?.threadId) {
        localStorage.removeItem(`ahafood.chat.messages.${info.threadId}`);
      }
      const newThreadId = nanoid();
      const newInfo: ThreadInfo = {
        threadId: newThreadId,
        userId,
        lastMessageTime: lastMessageTime ?? now,
      };
      localStorage.setItem(THREAD_KEY, JSON.stringify(newInfo));
      setThreadId(newThreadId);
      lastUserId.current = userId;
    } else if (info) {
      setThreadId(info.threadId);
      lastUserId.current = info.userId;
    } else {
      setThreadId(null);
      lastUserId.current = null;
    }
  }, [userId, lastMessageTime]);

  // Hàm bắt đầu lại thread
  const restartThread = useCallback(() => {
    if (!userId) return;
    // Xoá messages của thread cũ nếu có
    if (threadId) {
      localStorage.removeItem(`ahafood.chat.messages.${threadId}`);
    }
    const newThreadId = nanoid();
    const now = Date.now();
    const newInfo: ThreadInfo = {
      threadId: newThreadId,
      userId,
      lastMessageTime: now,
    };
    localStorage.setItem(THREAD_KEY, JSON.stringify(newInfo));
    setThreadId(newThreadId);
    lastUserId.current = userId;
  }, [userId, threadId]);

  // Cập nhật thời gian tin nhắn cuối cùng
  const updateLastMessageTime = useCallback(
    (time: number) => {
      if (!userId || !threadId) return;
      const raw = localStorage.getItem(THREAD_KEY);
      let info: ThreadInfo | null = null;
      if (raw) {
        try {
          info = JSON.parse(raw);
        } catch {
          /* empty */
        }
      }
      if (info && info.threadId === threadId) {
        info.lastMessageTime = time;
        localStorage.setItem(THREAD_KEY, JSON.stringify(info));
      }
    },
    [userId, threadId]
  );

  return { threadId, restartThread, updateLastMessageTime };
}
